import React, { useEffect, useMemo, useState } from 'react';
import { findDepartmentsMissingCompliances, getDepartments, type Department } from '../lib/departments';
import { DepartmentContext, type DepartmentContextType } from './DepartmentContext';
import { useAuth } from './useAuth';

export const DepartmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authUser, loading: authLoading, isAdmin } = useAuth();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingComplianceConfigurations, setMissingComplianceConfigurations] = useState<Department[]>([]);

  useEffect(() => {
    if (authLoading) return;

    if (!authUser) {
      setDepartments([]);
      setMissingComplianceConfigurations([]);
      setError(null);
      setLoading(false);
      return;
    }

    const fetchDepartments = async () => {
      setLoading(true);
      setError(null);
      try {
        const departmentList = await getDepartments();
        const missing = findDepartmentsMissingCompliances(departmentList);
        setDepartments(departmentList);
        setMissingComplianceConfigurations(missing);
        if (missing.length && isAdmin) {
          console.warn('Departments missing compliance configuration', missing.map((dept) => dept.id));
        }
      } catch (err) {
        setError('Failed to fetch departments');
      } finally {
        setLoading(false);
      }
    };

    fetchDepartments();
  }, [authLoading, authUser]);

  const value = useMemo<DepartmentContextType>(
    () => ({
      departments,
      loading,
      error,
      missingComplianceConfigurations,
    }),
    [departments, loading, error, missingComplianceConfigurations],
  );

  return <DepartmentContext.Provider value={value}>{children}</DepartmentContext.Provider>;
};